import { useRef, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { App } from 'antd';
import type { TableProps } from 'antd';
import { MOD_STATUS_BULK_LIMIT } from '@/components/user_catalog/modStatusBulk';

interface SelectableRow {
  id: number;
  name?: string | null;
}

export interface BulkRowSelection<T> {
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  rowSelection: NonNullable<TableProps<T>['rowSelection']>;
  /** Name of a selected row, also one picked on another page. */
  labelOf: (id: number) => string | undefined;
  limit: number;
}

/**
 * Row selection for a bulk moderation toolbar, capped at what one request
 * may carry (`MOD_STATUS_BULK_LIMIT`). Selection survives paging; once the
 * cap is reached the remaining checkboxes are disabled, and a select-all
 * that would cross it is cut to the cap with a warning.
 */
export const useBulkRowSelection = <T extends SelectableRow>(
  limit: number = MOD_STATUS_BULK_LIMIT
): BulkRowSelection<T> => {
  const t = useTranslate();
  const { message } = App.useApp();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // Names of rows ever selected — antd hands over rows of other pages too
  // (preserveSelectedRowKeys), but only while they are selected.
  const names = useRef(new Map<number, string>());

  const atLimit = selectedIds.length >= limit;

  return {
    selectedIds,
    setSelectedIds,
    rowSelection: {
      selectedRowKeys: selectedIds,
      preserveSelectedRowKeys: true,
      onChange: (keys, rows) => {
        for (const row of rows) {
          if (row?.name) names.current.set(row.id, row.name);
        }
        const ids = keys.map(Number);
        if (ids.length > limit) {
          message.warning(
            t('user_catalog.common.bulk_mod_status.limit_reached', { limit })
          );
        }
        setSelectedIds(ids.slice(0, limit));
      },
      getCheckboxProps: (record) => ({
        disabled: atLimit && !selectedIds.includes(record.id),
      }),
    },
    labelOf: (id) => names.current.get(id),
    limit,
  };
};
